import classNames from 'classnames';
import { type FC, type PropsWithChildren } from 'react';

import CaretCircleDown from '@/components/Icons/CaretCircleDown';

import AddButton from './AddButton';
import s from './TableCell.module.scss';

export type THProps = {
  className?: string;
  compact?: never;
  colSpan?: number;
  head: true;
  label?: string;
  addButton?: never;
} & (
  | {
      columnId: string;
      sort: {
        onSort: (columnId: string, direction: 'asc' | 'desc') => unknown;
        currentSortByField: string;
        currentSortDirection: 'asc' | 'desc';
      };
    }
  | {
      columnId?: never;
      sort?: never;
    }
);

export type TDProps = {
  compact?: boolean;
  head?: false;
  label?: string;
  columnId?: never;
  sort?: never;
  colSpan?: number;
  className?: string;
  addButton?: Parameters<typeof AddButton>[0];
};

type Props = THProps | TDProps;

const TableCell: FC<PropsWithChildren<Props>> = ({
  addButton,
  children,
  className,
  columnId,
  colSpan,
  compact,
  head,
  label,
  sort,
}) => {
  const handleSortClick = (): void => {
    if (sort) {
      // if changing column, sort asc.
      // if not changing column, toggle.
      let newDirection: 'asc' | 'desc' = 'desc';
      if (columnId === sort.currentSortByField) {
        newDirection = sort.currentSortDirection === 'asc' ? 'desc' : 'asc';
      }
      sort.onSort(columnId, newDirection);
    }
  };

  const isSortable = head && sort;
  const showSort = isSortable && columnId === sort.currentSortByField;
  const Tag = head ? 'th' : 'td';
  const ButtonOrDiv = isSortable ? 'button' : 'div';

  return (
    <Tag className={classNames(s.tag, colSpan && (head ? s.sectionTH : s.section), className)} colSpan={colSpan}>
      <ButtonOrDiv
        className={isSortable ? s.cellButton : s.cell}
        {...(head && sort ? { onClick: handleSortClick } : {})}
      >
        <div>
          {label && (
            <p
              className={classNames(
                compact || head ? 'caption' : 'body-sm',
                s.label,
                (head && (showSort ? s.labelHighlighted : s.labelRegular)) || '',
              )}
            >
              {label}
            </p>
          )}
          {children}
        </div>
        {addButton && <AddButton {...addButton} />}
        {showSort && (
          <div className={classNames(s.sort, sort.currentSortDirection === 'asc' ? s.sortAsc : s.sortDesc)}>
            <CaretCircleDown size={16} weight='fill' fill='var(--color-gray-100)' />
          </div>
        )}
      </ButtonOrDiv>
    </Tag>
  );
};

export default TableCell;
